import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { getDrawerBackdropClass, getDrawerPanelClass } from "../lib/design-system";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Wrench,
  Check,
  Clock,
  Gauge,
  ShieldAlert,
  User,
  Package,
  HardHat,
  ListChecks,
  Loader2,
} from "lucide-react";
import { generateMaintenanceGuide } from "../services/maintenance-guide.api";

/** Shape of a step as stored on the maintenance task at generation time. */
export interface PreloadedDIYStep {
  title: string;
  description: string;
  instructions?: string[];
  safetyNote?: string;
  toolsNeeded?: string[];
  duration?: string;
  riskLevel?: string;
}

/** Shape of a part as stored on the maintenance task at generation time. */
export interface PreloadedDIYPart {
  name: string;
  qty?: number;
  quantity?: number;
  description?: string;
  price?: number;
}

/** Normalized step used internally by the drawer — the single canonical shape. */
interface CanonicalStep {
  title: string;
  description: string;
  instructions: string[];
  toolsNeeded: string[];
  safetyNote?: string;
  duration?: string;
  riskLevel?: string;
}

interface DIYMaintenanceDrawerProps {
  maintenanceTitle: string;
  assetName: string;
  assetLocation?: string | null;
  assetBrand?: string;
  assetModel?: string;
  /**
   * DIY steps already generated at maintenance-task creation time.
   * When provided, the drawer will use them directly and skip the
   * `/v1/ai/generate-maintenance-guide` OpenAI call.
   */
  preloadedSteps?: PreloadedDIYStep[];
  /** Parts stored alongside the task (partsJson) — shown in "Before you start". */
  preloadedParts?: PreloadedDIYPart[];
  /** Optional metadata stored alongside the task (used in the header). */
  preloadedDifficulty?: string | null;
  preloadedDurationMinutes?: number | null;
  /** Total estimated cost for the task (e.g. "$25"). */
  estimatedCost?: string | null;
  /** When false, the task is best handled by a professional. */
  diyAvailable?: boolean;
  onClose: () => void;
  onComplete?: () => void;
  onBookTechnician?: () => void;
  _onOrderParts?: () => void;
}

type Difficulty = "Easy" | "Medium" | "Hard";
type Risk = "Low" | "Medium" | "High";

/**
 * Map a stored task difficulty ("easy"/"medium"/"hard" or already capitalized)
 * to the drawer's display variant.
 */
function normalizeDifficulty(value?: string | null): Difficulty {
  if (!value) return "Easy";
  const v = value.toLowerCase();
  if (v.startsWith("hard") || v === "advanced" || v === "difficult") return "Hard";
  if (v.startsWith("med") || v === "intermediate" || v === "moderate") return "Medium";
  return "Easy";
}

/** Parse the leading integer out of a duration string like "5 minutes" → 5. */
function parseMinutes(value?: string): number {
  if (!value) return 0;
  const m = /(\d+)/.exec(value);
  return m ? parseInt(m[1], 10) : 0;
}

/** Sum per-step durations into a single "X min" label. */
function sumDurations(steps: CanonicalStep[], fallbackMinutes?: number | null): string {
  const total = steps.reduce((acc, s) => acc + parseMinutes(s.duration), 0);
  if (total > 0) return `${total} min`;
  if (fallbackMinutes && fallbackMinutes > 0) return `${fallbackMinutes} min`;
  return "15–20 min";
}

/** Aggregate the highest risk level across steps → "Low" | "Medium" | "High". */
function aggregateRisk(steps: CanonicalStep[]): Risk | null {
  let highest = 0;
  for (const s of steps) {
    const r = (s.riskLevel ?? "").toLowerCase();
    if (r.includes("high")) return "High";
    if (r.includes("medium") && highest < 2) highest = 2;
    else if (r.includes("low") && highest < 1) highest = 1;
  }
  if (highest === 2) return "Medium";
  if (highest === 1) return "Low";
  return null;
}

const DIFFICULTY_COLOR: Record<Difficulty, string> = {
  Easy: "text-emerald-600 dark:text-emerald-400",
  Medium: "text-amber-600 dark:text-amber-400",
  Hard: "text-rose-600 dark:text-rose-400",
};

const RISK_COLOR: Record<Risk, string> = {
  Low: "text-emerald-600 dark:text-emerald-400",
  Medium: "text-amber-600 dark:text-amber-400",
  High: "text-rose-600 dark:text-rose-400",
};

export function DIYMaintenanceDrawer({
  maintenanceTitle,
  assetName,
  assetLocation,
  assetBrand,
  assetModel,
  preloadedSteps,
  preloadedParts,
  preloadedDifficulty,
  preloadedDurationMinutes,
  estimatedCost,
  diyAvailable = true,
  onClose,
  onComplete,
  onBookTechnician,
  _onOrderParts,
}: Readonly<DIYMaintenanceDrawerProps>) {
  const { t } = useTranslation("maintenance");
  const hasPreloaded = !!(preloadedSteps && preloadedSteps.length > 0);
  const [loading, setLoading] = useState(!hasPreloaded);
  const [steps, setSteps] = useState<CanonicalStep[]>(
    hasPreloaded ? mapPreloaded(preloadedSteps!) : [],
  );
  const [difficulty, setDifficulty] = useState<Difficulty>(
    normalizeDifficulty(preloadedDifficulty),
  );
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [prepOpen, setPrepOpen] = useState(true);

  useEffect(() => {
    if (hasPreloaded) return;
    loadGuide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenanceTitle, assetName, hasPreloaded]);

  const loadGuide = async () => {
    setLoading(true);
    try {
      const data = await generateMaintenanceGuide({
        maintenanceTitle,
        assetName,
        assetBrand,
        assetModel,
      });
      setSteps(
        data.steps.map((s) => ({
          title: s.title,
          description: s.description,
          instructions: [],
          toolsNeeded: s.tools ?? [],
          safetyNote: s.safety,
        })),
      );
      setDifficulty(data.difficulty);
    } catch (error) {
      console.error("Failed to load guide:", error);
    } finally {
      setLoading(false);
    }
  };

  const isLastStep = currentStepIndex >= steps.length - 1;

  const handleNextStep = () => {
    if (steps.length === 0) return;
    if (!isLastStep) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      onComplete?.();
      onClose();
    }
  };

  const handlePrevStep = () => {
    if (currentStepIndex > 0) setCurrentStepIndex((prev) => prev - 1);
  };

  const bookTechnician = () => {
    onBookTechnician?.();
    onClose();
  };

  const progressPercent = steps.length
    ? Math.round(((currentStepIndex + 1) / steps.length) * 100)
    : 0;

  const currentStep = steps[currentStepIndex];

  // ── Derived overview data (persists across all steps) ──────────────────────
  const allTools = Array.from(
    new Set(steps.flatMap((s) => s.toolsNeeded).filter(Boolean)),
  );
  const parts = (preloadedParts ?? []).map((p) => ({
    name: p.name,
    qty: p.qty ?? p.quantity ?? 1,
    description: p.description,
  }));
  const durationLabel = sumDurations(steps, preloadedDurationMinutes);
  const risk = aggregateRisk(steps);
  const prepCount = allTools.length + parts.length;

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        className={getDrawerBackdropClass(true)}
        onClick={onClose}
        aria-label={t("diy.closeAria")}
      />

      {/* Drawer */}
      <div className={cn(getDrawerPanelClass(true, "lg"), "animate-slide-in-right")}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="shrink-0 border-b border-border bg-card">
          <div className="flex items-center gap-3 px-5 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Wrench className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-[15px] font-semibold leading-tight text-foreground">
                {maintenanceTitle}
              </h2>
              <p className="truncate text-xs text-muted-foreground">
                {assetName}
                {assetLocation ? ` · ${assetLocation}` : ""}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label={t("diy.closeAria")}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Progress */}
          {steps.length > 0 && !loading && (
            <div className="px-5 pb-4">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-medium text-foreground">
                  {t("diy.stepXOfY", { current: currentStepIndex + 1, total: steps.length })}
                </span>
                <span className="text-xs font-medium text-muted-foreground">
                  {t("diy.percentComplete", { percent: progressPercent })}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className="flex-1 space-y-4 overflow-y-auto bg-background p-5">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-3 py-24">
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{t("diy.generatingGuide")}</p>
            </div>
          )}

          {!loading && steps.length === 0 && (
            <div className="py-24 text-center text-sm text-muted-foreground">
              {t("diy.failedToLoad")}
            </div>
          )}

          {!loading && steps.length > 0 && (
            <>
              {/* Overview stat tiles */}
              <div className="grid grid-cols-3 divide-x divide-border overflow-hidden rounded-2xl border border-border bg-card">
                <StatTile
                  icon={<Clock className="h-4 w-4" />}
                  label={t("diy.duration")}
                  value={durationLabel}
                />
                <StatTile
                  icon={<Gauge className="h-4 w-4" />}
                  label={t("diy.difficulty")}
                  value={difficulty}
                  valueClass={DIFFICULTY_COLOR[difficulty]}
                />
                <StatTile
                  icon={<ShieldAlert className="h-4 w-4" />}
                  label={t("diy.riskLevel")}
                  value={risk ?? "—"}
                  valueClass={risk ? RISK_COLOR[risk] : undefined}
                />
              </div>

              {/* Professional recommendation */}
              {!diyAvailable && (
                <div className="flex items-start gap-3 rounded-2xl border border-amber-300/60 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    <HardHat className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                      Best handled by a professional
                    </p>
                    <p className="mt-0.5 text-xs leading-relaxed text-amber-800/90 dark:text-amber-300/80">
                      This task can be risky or complex. You can still follow the steps below, but
                      we recommend booking a certified technician.
                    </p>
                    {onBookTechnician && (
                      <button
                        onClick={bookTechnician}
                        className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
                      >
                        <User className="h-3.5 w-3.5" />
                        Book a technician
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Before you start (collapsible) */}
              {prepCount > 0 && (
                <div className="overflow-hidden rounded-2xl border border-border bg-card">
                  <button
                    type="button"
                    onClick={() => setPrepOpen((o) => !o)}
                    className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-secondary/40"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      Before you start
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                        {prepCount}
                      </span>
                    </span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 text-muted-foreground transition-transform duration-200",
                        prepOpen && "rotate-180",
                      )}
                    />
                  </button>

                  {prepOpen && (
                    <div className="space-y-4 border-t border-border px-4 pb-4 pt-4">
                      {/* Tools */}
                      {allTools.length > 0 && (
                        <div>
                          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Tools you&apos;ll need
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {allTools.map((tool) => (
                              <span
                                key={tool}
                                className="inline-flex items-center gap-1 rounded-lg border border-border bg-secondary/50 px-2.5 py-1 text-xs font-medium text-foreground"
                              >
                                <Wrench className="h-3 w-3 text-muted-foreground" />
                                {tool}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Parts */}
                      {parts.length > 0 && (
                        <div>
                          <div className="mb-2 flex items-center justify-between">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Parts you&apos;ll need
                            </p>
                            {estimatedCost && (
                              <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                                ~{estimatedCost}
                              </span>
                            )}
                          </div>
                          <ul className="space-y-2">
                            {parts.map((p) => (
                              <li key={p.name} className="flex items-start gap-2.5 text-sm">
                                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                                <span className="text-foreground">
                                  <span className="font-medium">{p.name}</span>
                                  {p.qty > 1 && (
                                    <span className="text-muted-foreground"> ×{p.qty}</span>
                                  )}
                                  {p.description && (
                                    <span className="text-muted-foreground"> — {p.description}</span>
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                          {_onOrderParts && (
                            <button
                              onClick={_onOrderParts}
                              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-secondary"
                            >
                              <Package className="h-3.5 w-3.5" />
                              Order parts
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Current step (hero) */}
              {currentStep && (
                <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                  {/* Step head */}
                  <div className="flex items-start gap-3.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-base font-bold text-primary-foreground">
                      {currentStepIndex + 1}
                    </div>
                    <div className="flex-1 pt-0.5">
                      <h3 className="text-base font-semibold leading-snug text-foreground">
                        {currentStep.title}
                      </h3>
                      {currentStep.description && (
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {currentStep.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Instructions */}
                  {currentStep.instructions.length > 0 && (
                    <div className="mt-5">
                      <p className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <ListChecks className="h-3.5 w-3.5" />
                        Instructions
                      </p>
                      <ol className="space-y-3">
                        {currentStep.instructions.map((ins, i) => (
                          <li key={ins} className="flex items-start gap-3">
                            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                              {i + 1}
                            </span>
                            <p className="flex-1 text-sm leading-relaxed text-foreground">{ins}</p>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Safety note */}
                  {currentStep.safetyNote && (
                    <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3.5 dark:border-amber-900/40 dark:bg-amber-950/20">
                      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                      <div>
                        <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                          {t("diy.safetyLabel")}
                        </p>
                        <p className="mt-0.5 text-xs leading-relaxed text-amber-800/90 dark:text-amber-300/80">
                          {currentStep.safetyNote}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step dots */}
              {steps.length > 1 && (
                <div className="flex items-center justify-center gap-1.5 pt-1">
                  {steps.map((step, i) => (
                    <div
                      key={step.title}
                      className={cn(
                        "h-1.5 rounded-full transition-all duration-300",
                        i === currentStepIndex
                          ? "w-6 bg-primary"
                          : i < currentStepIndex
                            ? "w-1.5 bg-primary/50"
                            : "w-1.5 bg-muted",
                      )}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        {!loading && steps.length > 0 && (
          <div className="shrink-0 border-t border-border bg-card px-5 py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrevStep}
                disabled={currentStepIndex === 0}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-border px-4 text-sm font-semibold text-foreground transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                {t("diy.backLabel", "Back")}
              </button>
              <button
                onClick={handleNextStep}
                className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:opacity-90"
              >
                {isLastStep ? (
                  <>
                    <Check className="h-4 w-4" />
                    {t("diy.completeGuideButton")}
                  </>
                ) : (
                  <>
                    {t("diy.nextStepButton")}
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
            {isLastStep && onBookTechnician && (
              <button
                onClick={bookTechnician}
                className="mt-2.5 flex w-full items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <User className="h-3.5 w-3.5" />
                {t("diy.bookTechnicianButton")}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/** Compact stat tile used in the overview strip. */
function StatTile({
  icon,
  label,
  value,
  valueClass,
}: Readonly<{ icon: React.ReactNode; label: string; value: string; valueClass?: string }>) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-2 py-3.5 text-center">
      <span className="text-muted-foreground">{icon}</span>
      <span className={cn("text-sm font-bold leading-none", valueClass ?? "text-foreground")}>
        {value}
      </span>
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
    </div>
  );
}

/** Map preloaded task steps → canonical steps. */
function mapPreloaded(steps: PreloadedDIYStep[]): CanonicalStep[] {
  return steps.map((s) => ({
    title: s.title,
    description: s.description,
    instructions: s.instructions ?? [],
    toolsNeeded: s.toolsNeeded ?? [],
    safetyNote: s.safetyNote,
    duration: s.duration,
    riskLevel: s.riskLevel,
  }));
}
